
import { useState } from 'react';

export default function FactoryApplicationSection() {
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    position: '',
    phone: '',
    email: '',
    address: '',
    businessLicense: '',
    establishedYear: '',
    employees: '',
    productionCapacity: '',
    mainProducts: '',
    certifications: '',
    cooperationModel: '',
    monthlyCapacity: '',
    qualitySystem: '',
    factoryArea: '',
    introduction: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSubmit = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSubmit.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d35eb9l6e4fn5h2hkpo0', {
        method: 'POST',
        body: new URLSearchParams(formDataToSubmit as any)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          companyName: '',
          contactPerson: '',
          position: '',
          phone: '',
          email: '',
          address: '',
          businessLicense: '',
          establishedYear: '',
          employees: '',
          productionCapacity: '',
          mainProducts: '',
          certifications: '',
          cooperationModel: '',
          monthlyCapacity: '',
          qualitySystem: '',
          factoryArea: '',
          introduction: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const applicationProcess = [
    {
      step: '01',
      title: '提交申请',
      description: '填写详细的工厂信息和合作申请表单',
      icon: 'ri-file-text-line'
    },
    {
      step: '02',
      title: '资格审核',
      description: '我们会对工厂资质和生产能力进行评估',
      icon: 'ri-search-line'
    },
    {
      step: '03',
      title: '实地考察',
      description: '安排专业团队实地考察工厂设施和管理',
      icon: 'ri-map-pin-line'
    },
    {
      step: '04',
      title: '合作洽谈',
      description: '确定具体的合作模式和合同条款',
      icon: 'ri-handshake-line'
    },
    {
      step: '05',
      title: '签约合作',
      description: '签署正式合作协议，开始合作关系',
      icon: 'ri-contract-line'
    }
  ];

  return (
    <section id="application" className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
      <div className="container mx-auto px-6 lg:px-16 xl:px-24">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
            申请合作
          </h2>
          <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto">
            填写工厂合作申请表单，开启我们的合作之旅
          </p>
        </div>

        {/* Application Process */}
        <div className="mb-16">
          <h3 className="text-xl md:text-2xl font-bold text-center text-gray-800 mb-8">申请流程</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-4">
            {applicationProcess.map((process, index) => (
              <div key={index} className="flex flex-col items-center text-center">
                <div className="relative mb-4">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center mb-2">
                    <i className={`${process.icon} text-white text-2xl`}></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {process.step}
                  </div>
                  {index < applicationProcess.length - 1 && (
                    <div className="hidden md:block absolute top-8 left-20 w-12 h-0.5 bg-gray-300"></div>
                  )}
                </div>
                <h4 className="font-bold text-gray-800 mb-2 text-base md:text-lg">{process.title}</h4>
                <p className="text-gray-600 text-xs md:text-sm">{process.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <form onSubmit={handleSubmit} data-readdy-form id="factory-cooperation-application">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* 企业基本信息 */}
                <div className="md:col-span-2 mb-6">
                  <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-4 border-b border-gray-200 pb-2">
                    <i className="ri-building-line mr-2 text-blue-600"></i>
                    企业基本信息
                  </h3>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm md:text-base">
                    企业名称 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                    placeholder="请输入企业全称"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm md:text-base">
                    联系人 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                    placeholder="请输入联系人姓名"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm md:text-base">
                    职务 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="position"
                    value={formData.position}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                    placeholder="请输入职务"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm md:text-base">
                    联系电话 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                    placeholder="请输入联系电话"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm md:text-base">
                    邮箱地址
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                    placeholder="请输入邮箱地址"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm md:text-base">
                    企业地址 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                    placeholder="请输入详细地址"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm md:text-base">
                    营业执照编号 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="businessLicense"
                    value={formData.businessLicense}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                    placeholder="请输入营业执照统一社会信用代码"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm md:text-base">
                    成立年份 <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="establishedYear"
                    value={formData.establishedYear}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm appearance-none bg-white cursor-pointer"
                    required
                  >
                    <option value="">请选择成立年份</option>
                    {Array.from({ length: 30 }, (_, i) => 2024 - i).map(year => (
                      <option key={year} value={year}>{year}年</option>
                    ))}
                  </select>
                </div>

                {/* 生产能力信息 */}
                <div className="md:col-span-2 mb-6 mt-6">
                  <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-4 border-b border-gray-200 pb-2">
                    <i className="ri-settings-line mr-2 text-blue-600"></i>
                    生产能力信息
                  </h3>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm md:text-base">
                    员工人数 <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="employees"
                    value={formData.employees}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm appearance-none bg-white cursor-pointer"
                    required
                  >
                    <option value="">请选择员工人数</option>
                    <option value="10-30人">10-30人</option>
                    <option value="30-50人">30-50人</option>
                    <option value="50-100人">50-100人</option>
                    <option value="100-200人">100-200人</option>
                    <option value="200人以上">200人以上</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm md:text-base">
                    年产能 <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="productionCapacity"
                    value={formData.productionCapacity}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm appearance-none bg-white cursor-pointer"
                    required
                  >
                    <option value="">请选择年产能</option>
                    <option value="50-100吨">50-100吨</option>
                    <option value="100-300吨">100-300吨</option>
                    <option value="300-500吨">300-500吨</option>
                    <option value="500-1000吨">500-1000吨</option>
                    <option value="1000吨以上">1000吨以上</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm md:text-base">
                    主要产品 <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="mainProducts"
                    value={formData.mainProducts}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm appearance-none bg-white cursor-pointer"
                    required
                  >
                    <option value="">请选择主要产品</option>
                    <option value="绿茶">绿茶</option>
                    <option value="红茶">红茶</option>
                    <option value="乌龙茶">乌龙茶</option>
                    <option value="普洱茶">普洱茶</option>
                    <option value="白茶">白茶</option>
                    <option value="黑茶">黑茶</option>
                    <option value="花茶">花茶</option>
                    <option value="多种茶类">多种茶类</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm md:text-base">
                    质量认证
                  </label>
                  <input
                    type="text"
                    name="certifications"
                    value={formData.certifications}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                    placeholder="请输入已获得的质量认证，如ISO9001、有机认证等"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm md:text-base">
                    工厂面积
                  </label>
                  <input
                    type="text"
                    name="factoryArea"
                    value={formData.factoryArea}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                    placeholder="请输入工厂总面积，单位：平方米"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm md:text-base">
                    质量管理体系
                  </label>
                  <select
                    name="qualitySystem"
                    value={formData.qualitySystem}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm appearance-none bg-white cursor-pointer"
                  >
                    <option value="">请选择质量管理体系</option>
                    <option value="已建立完善体系">已建立完善体系</option>
                    <option value="基本体系">基本体系</option>
                    <option value="正在建设中">正在建设中</option>
                    <option value="需要指导建设">需要指导建设</option>
                  </select>
                </div>

                {/* 合作意向 */}
                <div className="md:col-span-2 mb-6 mt-6">
                  <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-4 border-b border-gray-200 pb-2">
                    <i className="ri-handshake-line mr-2 text-blue-600"></i>
                    合作意向
                  </h3>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm md:text-base">
                    期望合作模式 <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="cooperationModel"
                    value={formData.cooperationModel}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm appearance-none bg-white cursor-pointer"
                    required
                  >
                    <option value="">请选择期望的合作模式</option>
                    <option value="OEM代工合作">OEM代工合作</option>
                    <option value="ODM产品开发">ODM产品开发</option>
                    <option value="战略伙伴合作">战略伙伴合作</option>
                    <option value="多种模式">多种模式</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm md:text-base">
                    月生产能力
                  </label>
                  <select
                    name="monthlyCapacity"
                    value={formData.monthlyCapacity}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-5 transition-colors text-sm appearance-none bg-white cursor-pointer"
                  >
                    <option value="">请选择月生产能力</option>
                    <option value="10吨以下">10吨以下</option>
                    <option value="10-30吨">10-30吨</option>
                    <option value="30-50吨">30-50吨</option>
                    <option value="50-100吨">50-100吨</option>
                    <option value="100吨以上">100吨以上</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-gray-700 font-medium mb-2 text-sm md:text-base">
                    企业介绍
                  </label>
                  <textarea
                    name="introduction"
                    value={formData.introduction}
                    onChange={handleInputChange}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm resize-none"
                    rows={4}
                    placeholder="请简要介绍企业发展历程、核心优势、合作期望等（限500字符）"
                  />
                  <div className="text-right text-xs md:text-sm text-gray-500 mt-1">
                    {formData.introduction.length}/500
                  </div>
                </div>
              </div>

              <div className="mt-8 text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-12 py-4 rounded-full font-semibold text-sm md:text-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      提交中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      提交申请
                    </>
                  )}
                </button>
              </div>

              {/* 提交状态提示 */}
              {submitStatus === 'success' && (
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center text-green-800">
                    <i className="ri-check-circle-line text-xl mr-2"></i>
                    <span className="font-medium text-sm md:text-base">申请提交成功！</span>
                  </div>
                  <p className="text-green-700 text-xs md:text-sm mt-1">
                    我们已收到您的合作申请，工作人员会在3个工作日内与您联系，请保持电话畅通。
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center text-red-800">
                    <i className="ri-error-warning-line text-xl mr-2"></i>
                    <span className="font-medium text-sm md:text-base">提交失败</span>
                  </div>
                  <p className="text-red-700 text-xs md:text-sm mt-1">
                    网络异常，请稍后重试或联系客服。
                  </p>
                </div>
              )}
            </form>
          </div>

          {/* 联系方式 */}
          <div className="mt-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-white text-center">
            <h3 className="text-xl md:text-2xl font-bold mb-4">更多联系方式</h3>
            <p className="text-blue-100 mb-6 text-sm md:text-base">
              如果您需要了解更多合作详情，欢迎通过以下方式联系我们
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <i className="ri-phone-line text-2xl mb-2"></i>
                <div className="font-semibold text-base md:text-lg">合作热线</div>
                <div className="text-blue-100 text-sm md:text-base">400-666-8888</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <i className="ri-mail-line text-2xl mb-2"></i>
                <div className="font-semibold text-base md:text-lg">商务邮箱</div>
                <div className="text-blue-100 text-sm md:text-base">94@nandian.cn</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <i className="ri-wechat-line text-2xl mb-2"></i>
                <div className="font-semibold text-base md:text-lg">微信客服</div>
                <div className="text-blue-100 text-sm md:text-base">NDG-factory</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
